// adapt and construct new patterns

import nsstr from 'nsstr'

import {
  gnaccNSMap as accNSMap,
  gnaccSpecAdd as accSpecAdd
} from './gnacc.js'

import {
  gnarrReduce as arrReduce
} from './gnarr.js'

import {
  gnenumTYPE as TYPE,
  gnenumNSFKEY as NSFKEY,
  gnenumNSSUCH as NSSUCH,
  gnenumNSFKEYBASEELEM as NSFKEYBASEELEM,
  gnenumKEYFROM as KEYFROM,
  gnenumKEYBASEORIGIN as KEYBASEORIGIN,
  gnenumPATHROOTSPECS as PATHROOTSPECS,
  gnenumEDGEINDISABLE as EDGEINDISABLE,
  gnenumSPECNODENAMEKEY as SPECNODENAMEKEY,
  gnenumSPECNODETYPENAVPATH as SPECNODETYPENAVPATH,
  gnenumSPECNODETYPEPATHLOCALREF as SPECNODETYPEPATHLOCALREF,
  gnenumUNSAFE as UNSAFE
} from './gnenum.js'

import {
  gnerr_nodespecchildinvalid,
  gnerr_nodespecobjinvalid
} from './gnerr.js'

import {
  gnrun
} from './gnrun.js'

import {
  gnutilPlainObjIs as utilPlainObjIs,
  gnutilNonNullObjIs as utilNonNullObjIs
} from './gnutil.js'

import {
  gniso_patternfrompath
} from './gniso.js'

const gnspec_nameisvalid = (key, type) => (
  type = typeof key,
  type === 'number' ||
    (type === 'string' && key.length > 0))

const gnspec_keyisvalid = (key, type) => (
  type = typeof key,
  type === 'number' ||
    (type === 'string' && key.length > 0))

// pathrule's are incomplete patterns with reference to more-complete
// patterns that can be resolved
const gnspec_traitispathrule = spec => (
  spec[SPECNODETYPENAVPATH])

const gnspec_atomisvalid = spec => Boolean(
  utilNonNullObjIs(spec)  &&
    typeof spec.name === 'string' && spec.name)

const gnspec_traitisvalid = utilNonNullObjIs

const gnspec_atomisdynamic = spec => (
  spec[TYPE])

// deserialise the nspropstr to definitions on named-properties
const gnspec_nspropstrparse = (nspropstr, parsed) => (
  parsed = nsstr(nspropstr),
  parsed.path = parsed.fnspath,
  parsed.prop = parsed.nsprop || parsed.nsstr,
  parsed)

// expands the below forms by merging the parsed
// prop values results with the given opts
//
// "gn:type" : "nsprop",
// "prop" : "baseelem.rotation",
//
// "gn:type" : "nsprop",
// "prop" : "[fkey.blogkey].subj.title",
//
const gnspec_nspropexpand = opts => {
  if (opts.isexpanded)
    return opts

  return Object.assign({
    isexpanded: true
  }, opts, opts.prop ? gnspec_nspropstrparse(opts.prop) : {})
}

const gnspec_nspropexpandall = optsarr => {
  if (!optsarr.length || optsarr[0].isexpanded)
    return optsarr

  return optsarr.map(gnspec_nspropexpand)
}

// const gnspec_getrefspecchild = refspec => Object.assign({
const gnspec_childrefcreate = refspec => Object.assign({
  [SPECNODENAMEKEY]: refspec[SPECNODENAMEKEY]
}, refspec.spec, {
  name: String(refspec.name || refspec.spec.name)
})

const gnspec_childrefget = (meta, index) => (
  meta[SPECNODENAMEKEY]
    ? gnspec_childrefcreate(meta, index)
    : meta.spec || meta)

// returns a unique name from a unique value on the data
const gnspec_childprenamefromnode = async (
  ss, cfg, gr, nd, spec, fkey, obj, i
) => {
  const keyprop = spec[KEYFROM]
  let name

  if (gnspec_keyisvalid(keyprop)) {
    name = gnspec_nameisvalid(obj[keyprop])
      && String(obj[keyprop])
  } else if (gnspec_traitisvalid(keyprop)) {
    // keyprop is a pattern, resolve
    name = await gnrun(ss, cfg, gr, nd, {
      baseelem: obj
    }, keyprop)
  }

  // fallback to dangerous, using index in name...
  return name || `${UNSAFE + i}-${spec.name}`
}

// Observe... separate node hierarchies may generate 'safe' names
// which collide later when joined at separate node hierarchies with
// other 'safe' names that resolve to same value.
//
// if problem surfaces and requires solution, this function should be updated
//
const gnspec_childnamecreate = (spec, key, i) => {
  const specname = spec.name
  const keybasenamei = key.lastIndexOf('/')
  const keybasename = keybasenamei > -1 ? key.slice(keybasenamei + 1) : key
  const specnamekey = specname + '_' + keybasename
  // dangerous, using index in name...
  return specname.startsWith(UNSAFE)
    ? `${UNSAFE + i}_${specnamekey}`
    : specnamekey
}

// Summary of fkey+datakey
//
// returns "fkey" object (not "fkeyhydrated") for the childpre spec
//
// `fkey` uses a necessarily complex behaviour with `base` datakey.
// A node with 'base' data generates child nodes by mapping base
// elements (datakey) to template child specs.
//
// Simplified description of what happens,
// ```
// childspecs = base.map(datakey => specwithdata(childspec, datakey))
// ```
//
// One eventually wants to link nodes with data. This is difficult because data
// are variable, eg
// ```
// { name: 'email1', node: 'uitext', subj: { value: '[/datausers/172].email' } }
// { name: 'email2', node: 'uitext', subj: { value: '[/datausers/837].email' } }
// ```
//
// Using 'fkey' a friendly-named reference, such as 'datauser', can be used
// inside the node and deeply inside any child nodes, as they inherit the
// 'fkey' (eg, iow fkey is scoped to the subtree)
// ```
// {
//   name: 'email1',
//   node: 'uitext',
//   fkey: { datauser: '/datausers/172' },
//   subj: { value: '[fkey.datauser].email' }
// }
// ```
//
// Programmatically building fkey values has challenges,
//  * App-author should control name of property, here 'datauser'
//    * should be simple enough to be hand-applied in literal JSON
//    * should allow an abstraction so App-tooling can guarantee uniqueness
//  * spec should not embed implied-dependencies on baseelem data
//  * fkey resolution should be local w/ only datakey and no hard parent link
//    * `resolve(childspec, datakey)`
//  * fkey details should be self-contained, relative, for loose node coupling
//  * fkey definition-sequence must not overwrite parent fkeys
//  * resolution sequence should be O(n) and avoid looping
//    * looping okay in first iteration, but should be possible to factor out
//
// --------------------------------------------------------
// Here's how it works currently, in tandem with spec tooling
//
// Two supported scenarious:
//   1. variable or query-generated key,
//   2. human-generated stringy key
//
//
// 1. query for fkey
//    ```
//    { fkey: d.nsobject(
//        dataItemKeyQuery, '[./].baselem',
//        'dataItemKeyStringy', '[./].baselem') }
//    ```
//
//    query resolves fkey, auto-generating '[./].baseelem' for base child
//    ```
//    { fkey: {
//        '[./].baseelem': ['datanodeVariable', 'datanodeStringy'],
//        dataitemVariable: 'baseelem4827',
//        dataItemKeyStringy: '[./].baselem'
//    } }
//    ```
//
//    note:
//     * dataItemKeyQuery resolves 'baselem-4827' for every usage in subtree,
//     * tooling always auto-defines fkey '[./].baseelem' for any base child
//     * `[./].baseelem` gives lookup for properties that reference baselem
//
//    client resolves fkey to this, `resolve(fkeynamespace, datakey)`,
//    ```
//    { fkey: {
//      '[./].baseelem': null,
//      baseelem4827: '/dataitems/1729362148779',
//      dataitemVariable: '/dataitems/1729362148779',
//      dataItemKeyStringy: '/dataitems/1729362148779'
//    } }
//    ```
//
// DONE:
//   * '[./].baseelem' hydrates as `null` to mute inheritance,
//   * 'baseelem4827' can be auto-created for child, if `dataItemKeyQuery` used,
//   * query tool ensures dataItemKeyQuery resolves "baselem-4827" for subtree,
//   * childs can resolve fkey "baselem-4827",
//   * foreign-nodes can resolve fkey
const gnspec_childprefkeyfromdatakey = (fkey, datakey) => {
  if (typeof datakey !== 'string')
    throw new Error('datakey must be a string')

  if (!fkey)
    return {}

  if (NSFKEYBASEELEM in fkey === false
    || fkey[NSFKEYBASEELEM] === null
  ) {
    return fkey
  }

  return fkey[NSFKEYBASEELEM].reduce((fkeyhydrated, baseelemrefkey) => {
    fkeyhydrated[baseelemrefkey] = datakey

    // Below condition was triggered w/ the following but was not covered w/
    // any test. gaco nsobject does not generate this form and directly-related
    // test is not found there. Need more context before changing or finalizing
    // anything here, commenting-out for now.
    // ```
    // fkey: {
    //   '[./].baseelem': ['datatodo', 'requrl'],
    //   datatodo: '[./].baseelem',
    //   requrl: '[/dataenv].subj.requrl'
    // }
    // ```
    //
    // if (fkey[baseelemrefkey] !== NSFKEYBASEELEM) {
    //   fkeyhydrated[fkey[baseelemrefkey]] = datakey
    // }
    return fkeyhydrated
  }, {
    [NSFKEYBASEELEM]: null
  })
}

// must not mutate original values
//
// `fkeyhydrated` is used (rather than `fkey`) so that keys mapped
// from parent are available for `fkey` definitions
const gnspec_childprefromspecobj = async (
  ss, cfg, gr, nd, spec, fkey, obj, i
) => {
  if (!gnspec_atomisvalid(spec))
    throw gnerr_nodespecchildinvalid(gr, nd, spec)
  if (!gnspec_traitisvalid(obj))
    throw gnerr_nodespecobjinvalid(gr, nd, spec, obj)

  // const init = Array.isArray(spec.init) ? spec.init.slice() : {}
  // const subj = Array.isArray(spec.subj) ? spec.subj.slice() : spec.subj
  const subj = spec.subj
  const name = await gnspec_childprenamefromnode(
    ss, cfg, gr, nd, spec, fkey, obj, i)

  // if non-dynamic type exists, assume it is found at front of list always
  // `{ spread: true }` is legacy-thing for specmob, indicating non-dynamic vals

  const subjfull = accSpecAdd(subj, obj)

  return Object.assign({}, spec, {
    baseelemhydrated: obj,
    name,
    subj: subjfull
  })
}

// for a list of layers... each layer has unique id (key) defined by parent
//
// 'mapsrckey' associated source data node w/ layer pattern here,
// used when selection or other state obtained from external data node
const gnspec_childprefromspeckey = (ss, cfg, gr, ndp, spec, fkey, dkey, i) => (
  // `fkeyhydrated` is used (rather than `fkey`) so that keys mapped
  // from parent are available for `fkey` definitions
  Object.assign({ [KEYBASEORIGIN]: dkey }, spec, {
    name: gnspec_childnamecreate(spec, dkey, i),
    fkeyhydrated: gnspec_childprefkeyfromdatakey(fkey, dkey)
  })
)

const gnspec_childprefromspec = async (
  ss, cfg, gr, nd, data, i, specref, spec
) => (
  typeof data === 'string'
    ? gnspec_childprefromspeckey(ss, cfg, gr, nd, spec, specref.fkey, data, i)
    : gnspec_childprefromspecobj(ss, cfg, gr, nd, spec, specref.fkey, data, i))

const gnspec_datakeyset = (spec, key, datakey, dataval) => (
  spec[key] = spec[key] || {},
  spec[key] = Array.isArray(spec[key])
    ? (spec[key].push({ datakey: dataval }), spec[key])
    : (spec[key][datakey] = dataval, spec[key]),
  spec)

const gnspec_datakeyhydratedset = (spec, key, datakey, dataval) => (
  gnspec_datakeyset(spec, accNSMap[key].name, datakey, dataval))

const gnspec_datakeysetdeep = (spec, key, datakey, dataval) => (
  spec = gnspec_datakeyset(spec, key, datakey, dataval),
  spec = gnspec_datakeyhydratedset(spec, key, datakey, dataval),
  spec)

const gnspec_nsspecreduce = async (nsspec, subj, acc = [], accumfn) => {
  const proplen = Object.keys(subj || {}).length
  const nsspecs = Array.isArray(nsspec) ? nsspec : [nsspec]

  if (!nsspec || proplen === acc.length)
    return acc

  return arrReduce(nsspecs, async (acc, nsspec) => {
    if (gnspec_atomisdynamic(nsspec) && nsspec[EDGEINDISABLE])
      return acc

    const specnames = gnspec_atomisdynamic(nsspec)
      ? [nsspec.name]
      : Object.keys(nsspec)

    return arrReduce(specnames, async (acc, specname) => (
      accumfn(acc, subj, nsspec, specname)), acc)
  }, acc)
}

// 'prenode' is the early node stage
//
//  * stage 1: 'paths', a path, such as '/main'
//
//  * stage 2: 'pathdefs', a path definition that includes the path and other
//             information, such as rules or permissions which might
//             associate with the path, available child paths,
//             routing-details etc.
//
//  * stage 3: 'prenodes', completed patterns that define nodes,
//             originating from path definitions
//
//  * stage 4: 'nodes', completed and constructed from prenode-patterns
//
//
// perhaps the steps could be shown as below,
//
//   path => pathdef(s) => prenodes => nodes => vnodes
//
//
// ---------------------------------------------------------------------------
//
// these functions have been difficult to organise for many reason...
// names such as 'node', 'pattern' and 'path' already have specific meanings
// within gani and usage of those words here is confusing. incredible how
// labelling becomes difficult
//
// the relationhip these functions have with configuration and node files
// tends to result in circular dependencies to those files
//
// writing descriptive names for these functions is tough, for example, at time
// of this writing, gani_cfg includes functions with these signatures (yikes)

// if given pattern references an external pattern, handles the
// condition of obtaining the final pattern
const gnspec_pathseedcreate = async (ss, cfg, pathrule) => {
  const pathpattern = PATHROOTSPECS + pathrule.pathpattern

  // condition not used by web site, no test hitting this. to be removed?
  if (!pathpattern)
    return pathrule

  const prenodepattern = await gniso_patternfrompath(ss, cfg, pathpattern)
  return Object.assign(prenodepattern, {
    [SPECNODETYPENAVPATH]: true
  })
}

// recursively construct pattern nodes from path nodes
// until empty path node list
//
// maybe the entire list should be returned.... callers wanting the primary
// spec could find it at the head of the list.
//
// async-style callback patterns to be async accessed
const gnspec_pathseedstackcreate = async (ss, cfg, rules, obj, parent, arr) => {
  if (!rules[0])
    return arr || []

  const prenodespec = await gnspec_pathseedcreate(ss, cfg, rules[0])

  let spec = Object.assign({}, prenodespec)

  if (rules[0].matchedpathid) {
    let [matchkey, matchval] = rules[0].matchedpathid

    spec = gnspec_datakeysetdeep(spec, NSFKEY, matchkey, matchval)
  }

  if (parent) {
    parent.child = (parent.child || [])
      // only one path may exist. replaces any existing 'pathnode'
      // with _this_ path node
      .map(ospec => gnspec_traitispathrule(ospec) ? spec : ospec)
  } else {
    obj = spec
  }

  arr = arr || []
  arr.push(spec)

  return gnspec_pathseedstackcreate(
    ss, cfg, rules.slice(1), obj, spec, arr)
}

const gnspec_traitisexternal = spec => (
  spec && spec[TYPE] === SPECNODETYPEPATHLOCALREF)

// return full trait from local-ref,
// 'trait' to distinguish from other resolvable-types
//
// ex,
// ```
// {
//   'gn:type': "local-ref",
//   path: "../root-data-errors/eng-US.json"
// }
// ```
const gnspec_traitresolve = async (ss, cfg, spec) => (
  cfg.pagespecfromisopath(
    ss, cfg, spec.path.startsWith('../')
      ? PATHROOTSPECS + spec.path.slice(3)
      : spec.path))

const gnspec_pathseedanycreate = async (sess, cfg, pathdefarr) => {
  // this should be simplified/improved with more tests
  // maybe the empty-list condition does not need to be supported
  return pathdefarr.length
    ? await gnspec_pathseedstackcreate(sess, cfg, pathdefarr)
    : [await gnspec_pathseedcreate(sess, cfg, { pathpattern: 'root' })]
}

// eg, ['nsref', ['subj.label']]
const gnspec_recnsrefresolve = rec => {
  const recargs = rec[1]
  if (!recargs[1])
    recargs.push(gnspec_nspropstrparse(recargs[0]))

  return recargs[1]
}

const gnspec_clientis = spec => (
  utilPlainObjIs(spec) && spec[TYPE] === 'client')

const gnspec_ischildsfold = (nspec, ns) => {
  // optional ns, as currently child fold behaviour only
  // fully supoorted at NSSUCH nns
  if (ns && ns !== NSSUCH)
    return false

  if (!gnspec_clientis(nspec))
    return false

  // TODO for 'such' namespace, an error should be thrown when
  // spec fails the below condition, either here or at the caller.
  //
  // for this ns, spec must adhere to this shape or result will be
  // unusual and hard-ro-resolve error
  const chain = nspec.chain
  const chainrec0 = chain[0]
  const chainrec1 = chain[1]

  return (chainrec0 || [])[0] === 'nsref'
    && chainrec0[1][0].startsWith('[fkey.child')
    && (chainrec1 || [])[0] === 'childkeys'
    && (chain[2] || [])[0] === 'fold'
}

// {
//   'gn:type': 'client',
//   chain: [['nsref', ['[/path].subj.value']]]
// }
const gnspecClientNSREFIs = spec => (
  gnspec_clientis(spec) &&
    (spec.chain[0] || [])[0] === 'nsref')

const gnspecClientNSREFNTHIs = spec => (
  gnspecClientNSREFIs(spec) &&
    (spec.chain[1] || [])[0] === 'nth')

const gnspecClientNSREFNTHARG = spec => (
  gnspecClientNSREFIs(spec) &&
    (spec.chain[1] || [])[0] === 'nth' && spec.chain[1][1][0])


// assumes that nsref is first chain
const gnspecClientNSOBJGet = spec => {
  const chain = spec.chain
  const chainrecnsref = chain[0]
  const chainrecnsrefargs = chainrecnsref[1]

  // cached parse obj stored beside the stringy refrence
  if (!chainrecnsrefargs[1])
    chainrecnsrefargs.push(
      gnspec_nspropstrparse(chainrecnsrefargs[0]))

  return chainrecnsrefargs[1]
}

export {
  gnspec_clientis,
  gnspec_clientis as gnspecClientIs,
  gnspecClientNSREFIs,
  gnspecClientNSREFNTHIs,
  gnspecClientNSREFNTHARG,
  gnspec_ischildsfold,
  gnspec_recnsrefresolve,

  gnspec_keyisvalid,
  gnspec_traitisvalid,
  gnspec_traitisexternal,
  gnspec_traitispathrule,
  gnspec_traitresolve,
  gnspec_atomisvalid,
  gnspec_atomisdynamic,
  gnspec_nsspecreduce,

  gnspec_nspropstrparse,
  gnspec_nspropexpand,
  gnspec_nspropexpandall,

  gnspec_childrefcreate,
  gnspec_childrefget,
  gnspec_childnamecreate,

  gnspec_childprenamefromnode,
  gnspec_childprefromspecobj,
  gnspec_childprefromspeckey,
  gnspec_childprefromspec,
  gnspec_childprefkeyfromdatakey,

  gnspec_datakeyset,
  gnspec_datakeyhydratedset,

  gnspec_datakeysetdeep,

  gnspec_pathseedcreate,
  gnspec_pathseedstackcreate,
  gnspec_pathseedanycreate,
  gnspecClientNSOBJGet
}
